/**
 * Bill of Lading Locations Fields Component
 *
 * Renders the shipment locations section of the B/L form.
 * Extends OBLocationsFields with additional B/L-specific location types
 * (OIR = Origin Inland Routing, ODE = Outbound Delivery) and adds an
 * address sub-section and facilityCodeListProvider field.
 *
 * Location types:
 * - PLC = Place of Receipt
 * - POL = Port of Load
 * - POD = Port of Discharge
 * - PLD = Place of Delivery
 * - OIR = Origin Inland Routing
 * - ODE = Outbound Delivery
 */

import { useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { COUNTRY_OPTIONS } from '../../constants/countries';
import { getFieldIssues } from '../../utils/confidenceUtils';

const SEVERITY_COLORS = {
  error:   { border: 'border-l-red-500',   text: 'text-red-600',   icon: 'text-red-500' },
  warning: { border: 'border-l-amber-500', text: 'text-amber-600', icon: 'text-amber-500' },
  info:    { border: 'border-l-blue-500',  text: 'text-blue-600',  icon: 'text-blue-500' },
};

function formatIssueType(type) {
  if (!type) return 'Issue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function IssueIndicator({ issues = [] }) {
  if (issues.length === 0) return null;
  const worst = issues.some(i => i.severity === 'error') ? 'error'
    : issues.some(i => i.severity === 'warning') ? 'warning' : 'info';
  return (
    <span className="relative group/issues inline-flex">
      <svg className={`w-3.5 h-3.5 ${SEVERITY_COLORS[worst]?.icon || 'text-amber-500'} cursor-help`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/issues:block w-64">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {issues.map((issue, idx) => (
            <div key={idx} className={`px-3 py-2 border-l-4 ${SEVERITY_COLORS[issue.severity]?.border || 'border-l-gray-300'} ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
              <p className={`text-xs font-semibold ${SEVERITY_COLORS[issue.severity]?.text || 'text-gray-600'}`}>{formatIssueType(issue.type)}</p>
              {issue.detail && <p className="text-xs text-gray-600 mt-0.5">{issue.detail}</p>}
            </div>
          ))}
        </div>
      </div>
    </span>
  );
}

const LOCATION_TYPES = [
  { value: 'PLC', label: 'PLC - Place of Receipt' },
  { value: 'POL', label: 'POL - Port of Load' },
  { value: 'POD', label: 'POD - Port of Discharge' },
  { value: 'PLD', label: 'PLD - Place of Delivery' },
  { value: 'OIR', label: 'OIR - Origin Inland Routing' },
  { value: 'ODE', label: 'ODE - Outbound Delivery' }
];

const LOCATION_TYPE_LABELS = {
  PLC: 'Place of Receipt',
  POL: 'Port of Load',
  POD: 'Port of Discharge',
  PLD: 'Place of Delivery',
  OIR: 'Origin Inland Routing',
  ODE: 'Outbound Delivery'
};

/**
 * Collapsible address sub-section within each location card.
 * Kept collapsed by default since port locations often have no address.
 */
function AddressSection({ locationIndex, register, isEditMode, issues }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-t pt-3 mt-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800 mb-2"
      >
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Address (optional)
      </button>

      {isOpen && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-4">
          <div className="md:col-span-2">
            <Input
              label="Address Line 1"
              {...register(`shipmentLocations.${locationIndex}.address.addressLine1`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `shipmentLocations[${locationIndex}].address.addressLine1`)}
              placeholder="Street address"
            />
          </div>
          <div className="md:col-span-2">
            <Input
              label="Address Line 2"
              {...register(`shipmentLocations.${locationIndex}.address.addressLine2`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `shipmentLocations[${locationIndex}].address.addressLine2`)}
            />
          </div>
          <div className="md:col-span-2">
            <Input
              label="Address Line 3"
              {...register(`shipmentLocations.${locationIndex}.address.addressLine3`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `shipmentLocations[${locationIndex}].address.addressLine3`)}
            />
          </div>

          <Input
            label="City"
            {...register(`shipmentLocations.${locationIndex}.address.city`)}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, `shipmentLocations[${locationIndex}].address.city`)}
          />

          <Input
            label="State/Province"
            {...register(`shipmentLocations.${locationIndex}.address.state`)}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, `shipmentLocations[${locationIndex}].address.state`)}
          />

          <Input
            label="Postal Code"
            {...register(`shipmentLocations.${locationIndex}.address.postalCode`)}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, `shipmentLocations[${locationIndex}].address.postalCode`)}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span className="inline-flex items-center gap-1">
                Country
                <IssueIndicator issues={getFieldIssues(issues, `shipmentLocations[${locationIndex}].address.country`)} />
              </span>
            </label>
            <select
              {...register(`shipmentLocations.${locationIndex}.address.country`)}
              disabled={!isEditMode}
              className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
            >
              {COUNTRY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * BLLocationsFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {Function} props.watch - react-hook-form watch
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function BLLocationsFields({ control, register, watch, isEditMode, issues = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'shipmentLocations'
  });

  const addLocation = () => {
    append({
      locationType: 'POL',
      locationName: '',
      UNLocationCode: '',
      address: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      facilityCode: '',
      facilityCodeListProvider: ''
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">Shipment Locations</h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addLocation}>
            + Add Location
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No locations added yet. Click "Add Location" to specify ports and places.
        </div>
      )}

      {/* Location entries */}
      {fields.map((field, index) => (
        <div key={field.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
          {/* Location header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {LOCATION_TYPE_LABELS[field.locationType] || `Location #${index + 1}`}
            </span>
            {isEditMode && (
              <button type="button" onClick={() => remove(index)} className="text-red-500 hover:text-red-700 text-sm">
                Remove
              </button>
            )}
          </div>

          {/* Core location fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Location Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="inline-flex items-center gap-1">
                  Location Type
                  <IssueIndicator issues={getFieldIssues(issues, `shipmentLocations[${index}].locationType`)} />
                </span>
              </label>
              <select
                {...register(`shipmentLocations.${index}.locationType`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {LOCATION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <Input
              label="Location Name"
              {...register(`shipmentLocations.${index}.locationName`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `shipmentLocations[${index}].locationName`)}
              placeholder="e.g., Shanghai Port"
            />

            <Input
              label="UN Location Code"
              {...register(`shipmentLocations.${index}.UNLocationCode`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `shipmentLocations[${index}].UNLocationCode`)}
              placeholder="e.g., CNSHA"
            />

            <Input
              label="Facility Code"
              {...register(`shipmentLocations.${index}.facilityCode`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `shipmentLocations[${index}].facilityCode`)}
              placeholder="e.g., APMT"
            />

            <Input
              label="Facility Code List Provider"
              {...register(`shipmentLocations.${index}.facilityCodeListProvider`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `shipmentLocations[${index}].facilityCodeListProvider`)}
              placeholder="e.g., SMDG"
            />
          </div>

          {/* Collapsible address section */}
          <AddressSection
            locationIndex={index}
            register={register}
            isEditMode={isEditMode}
            issues={issues}
          />
        </div>
      ))}
    </div>
  );
}

export default BLLocationsFields;
